import { useForm } from 'react-hook-form';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DialogClose } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetPolicySummaryQueryKey,
  glacierPolicyApiReopenTransactionMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { formatErrorMessage } from '../../utils';

export const Reopen = ({ accountId }: { accountId: string }) => {
  const qc = useQueryClient();
  const permissionsQk = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: { account_id: accountId },
  });
  const summariesQk = glacierPolicyApiGetPolicySummaryQueryKey();
  const policyQk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const reopen = useMutation({
    ...glacierPolicyApiReopenTransactionMutation(),
    onSuccess() {
      qc.refetchQueries({ queryKey: permissionsQk });
      qc.refetchQueries({ queryKey: summariesQk });
      qc.refetchQueries({ queryKey: policyQk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] whitespace-pre-wrap break-words rounded-md bg-slate-950 p-4">
            <code className="text-white">Reopened</code>
          </pre>
        ),
      });
    },
    onError(error) {
      toast({
        title: 'Error',
        description: formatErrorMessage(
          error,
          'An error occurred while reopening the policy'
        ),
      });
    },
  });

  const form = useForm({});

  const { handleSubmit } = form;

  const onSubmit = () => {
    reopen.mutate({
      path: {
        account_id: accountId,
      },
    });
  };
  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Reopen Transaction</CardTitle>
          </CardHeader>
          <CardContent className="pb-4 pt-4">
            Click Submit to Reopen the Transaction
          </CardContent>
          <CardFooter className="flex justify-end">
            <DialogClose asChild>
              <Button
                type="submit"
                className="bg-brand-green text-brand-0 transition hover:bg-brand-green hover:opacity-70 hover:duration-300"
              >
                Submit
              </Button>
            </DialogClose>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};
